import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/turboclaim-logo.png";
import bgImage from "@/assets/login-background.jpeg";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, LoginForm } from "@/schemas/auth";

export default function Auth() {
  const [, setLocation] = useLocation();
  const { user, isLoading: authLoading, loginMutation } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      // Check for pending invite token
      const pendingToken = sessionStorage.getItem("pendingInviteToken");
      if (pendingToken) {
        // Redirect back to accept invitation page
        setLocation(`/accept-invitation/${pendingToken}`);
      } else {
        setLocation("/");
      }
    }
  }, [user, authLoading, setLocation]);

  const onSubmit = (data: LoginForm) => {
    // Use email as username for consistency
    loginMutation.mutate(
      {
        username: data.email,
        password: data.password,
      },
      {
        onSuccess: async () => {
          // Check for pending invite token
          const pendingToken = sessionStorage.getItem("pendingInviteToken");

          if (pendingToken) {
            // Will redirect via useEffect above to accept invitation
            return;
          }

          // User has authenticated, go to dashboard
          // The dashboard will check subscription status and redirect if needed
          setLocation("/");
        },
        onError: async (error) => {
          let message = error instanceof Error ? error.message : "Invalid credentials";

          if (error && typeof error === 'object' && 'response' in error) {
            const response = (error as any).response;
            if (response.status === 409) {
              // Try to parse error body
              try {
                const data = await response.json();
                if (data.message) {
                  message = data.message;
                } else {
                  message = "Account setup incomplete. Please contact support.";
                }
              } catch {
                message = "Account setup incomplete (409).";
              }
            }
          }

          toast({
            title: "Login failed",
            description: message,
            variant: "destructive",
          });
        },
      }
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-pulse">
            <img src={logo} alt="TurboClaim" className="h-12 mx-auto mb-4 opacity-60" />
          </div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] md:min-h-screen relative flex items-center justify-center">
      {/* Full Background with Image - No overlay for clear visibility */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundPosition: "center bottom",
        }}
      >
        {/* Very subtle dark overlay at top for contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1e3a5f]/40 via-transparent to-transparent" />
      </div>

      {/* Login Card - Centered */}
      <div className="relative z-10 w-full max-w-md mx-4 max-sm:my-8">
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          {/* Logo and Welcome */}
          <div className="text-center mb-6">
            <img
              src={logo}
              alt="TurboClaim"
              className="h-10 mx-auto mb-4"
              data-testid="img-logo-auth"
            />
            <h2 className="text-lg font-semibold text-gray-900">
              Welcome to TurboClaim
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Login or create an account to get started
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Email Field */}
            <div className="mb-4">
              <Label
                htmlFor="email"
                className="text-gray-700 font-medium text-sm"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                disabled={loginMutation.isPending}
                className={`mt-1.5 h-10 bg-gray-50/50 border-gray-300 focus:bg-white text-gray-900 ${errors.email ? 'border-red-500' : ''}`}
                data-testid="input-email"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="mb-6">
              <Label
                htmlFor="password"
                className="text-gray-700 font-medium text-sm"
              >
                Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  disabled={loginMutation.isPending}
                  className={`h-10 bg-gray-50/50 border-gray-300 focus:bg-white text-gray-900 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  data-testid="input-password"
                  {...register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                  data-testid="button-toggle-password"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              size="lg"
              className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
              disabled={loginMutation.isPending}
              data-testid="button-login"
            >
              {loginMutation.isPending ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </Button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center space-y-2">
            <div>
              <span className="text-sm text-gray-600">
                Don't have an account?{" "}
              </span>
              <a
                href="/signup"
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium"
                onClick={(e) => {
                  e.preventDefault();
                  setLocation("/signup");
                }}
                data-testid="link-signup"
              >
                Sign up
              </a>
            </div>
            <div>
              <span className="text-sm text-gray-600">
                Forgot your password?{" "}
              </span>
              <a
                href="/reset"
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium"
                onClick={(e) => {
                  e.preventDefault();
                  setLocation("/reset");
                }}
                data-testid="link-forgot-password"
              >
                Reset
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
